/* eslint-disable react/display-name */
import { Box, Flex, Text } from '@chakra-ui/core';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import { RiArrowDownSLine } from 'react-icons/ri';
import { CellProps, Column } from 'react-table';
import colors from '../../../../../styles/colors';
import { ComparisonViewModel } from '../utils/RequisitionDetailsViewModel';
import { SlotCell } from './cells/SlotCell';
import { ComparisonFlatRow, flattenRows } from './comparisonUtils';
import { MismatchBadge } from './components/MismatchBadge';
import { EmptyPlaceholder, GenericDataTable } from './components/TableComponents';

const useComparisonColumns = (t: (key: string) => string): Column<ComparisonFlatRow>[] =>
  useMemo<Column<ComparisonFlatRow>[]>(
    () => [
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('shiftHeader.day') as string,
        id: 'dayGroup',
        columns: [
          {
            Header: '',
            id: 'day',
            accessor: 'dayLabel',
            width: 90,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) => {
              const { isFirstSubRow, dayLabel, isDayRequired, isDayMismatch, desiredStart, desiredEnd } = row.original;
              if (!isFirstSubRow) return null;

              const hasData = desiredStart?.exists || desiredEnd?.exists;

              return (
                <Flex align="center" justify="space-between" width="100%">
                  <Flex align="center" gridGap="4px">
                    <Text fontSize="13px" fontWeight={hasData ? 700 : 400}>
                      {dayLabel}
                    </Text>
                    {isDayRequired && <FaCheck size={10} color={colors.gray[450]} />}
                  </Flex>
                  {isDayMismatch && <MismatchBadge />}
                </Flex>
              );
            },
          },
        ],
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('desiredAvailabilityWithMinHours') as string,
        id: 'desiredGroup',
        columns: [
          {
            Header: '',
            id: 'desiredStart',
            accessor: 'desiredStart',
            width: 110,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) => <SlotCell slot={row.original.desiredStart} />,
          },
          {
            Header: '',
            id: 'desiredEnd',
            accessor: 'desiredEnd',
            width: 110,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) => <SlotCell slot={row.original.desiredEnd} />,
          },
          {
            Header: '',
            id: 'minHours',
            accessor: 'minHoursLabel',
            width: 100,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) => {
              const { minHoursLabel, minHoursMismatch } = row.original;
              if (!minHoursLabel) return <EmptyPlaceholder />;
              return (
                <Flex align="center" justify="space-between" width="100%">
                  <Text fontSize="13px">{minHoursLabel}</Text>
                  {minHoursMismatch && <MismatchBadge />}
                </Flex>
              );
            },
          },
        ],
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
        Header: t('candidateAvailability') as string,
        id: 'candidateGroup',
        columns: [
          {
            Header: '',
            id: 'candidateStart',
            accessor: 'candidateStart',
            width: 110,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) =>
              row.original.candidateStart ? <Text fontSize="13px">{row.original.candidateStart}</Text> : <EmptyPlaceholder />,
          },
          {
            Header: '',
            id: 'candidateEnd',
            accessor: 'candidateEnd',
            width: 110,
            Cell: ({ row }: CellProps<ComparisonFlatRow>) =>
              row.original.candidateEnd ? <Text fontSize="13px">{row.original.candidateEnd}</Text> : <EmptyPlaceholder />,
          },
        ],
      },
    ],
    [t],
  );

// ─── main component ───────────────────────────────────────────────────────────

interface ComparisonTableProps {
  comparison: ComparisonViewModel;
}

export const ComparisionTable = memo(({ comparison }: ComparisonTableProps) => {
  const { t } = useTranslation('requisition');

  const { weekdayRows, weekendRows } = comparison;
  const columns = useComparisonColumns(t);

  const data = useMemo(() => flattenRows([...weekdayRows, ...weekendRows]), [weekdayRows, weekendRows]);
  const getRowBgColor = useCallback(
    (row: ComparisonFlatRow) => (row.isRowMismatch ? colors.requisitionDetails.mismatchRowBg : undefined),
    [],
  );

  const getCellProps = useCallback((row: ComparisonFlatRow, columnId: string, index: number) => {
    if (columnId === 'day') {
      if (row.isFirstSubRow) {
        return { rowSpan: row.subRowCount, verticalAlign: 'top' };
      }
      return { display: 'none' };
    }
    if (columnId === 'candidateStart' || columnId === 'candidateEnd') {
      const defaultBgColor = index % 2 === 0 ? colors.white : colors.gray_50;
      return { backgroundColor: defaultBgColor };
    }
    return {};
  }, []);

  const [showComparision, setShowComparision] = useState<boolean>(false);

  return (
    <Box my={3}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        cursor="pointer"
        data-testid="showReqAndCandidateAvabilityBtn"
        onClick={() => setShowComparision(!showComparision)}
        _hover={{ background: colors.gray[100] }}
        p="6px 0px"
      >
        <Text fontSize="14px" fontWeight={700} mb="6px">
          {t('compare')}
        </Text>
        <Box>
          <RiArrowDownSLine />
        </Box>
      </Flex>

      {showComparision && (
        <Box mt="16px" overflowX="auto" width="100%" sx={{ 'thead tr:last-of-type': { display: 'none' } }}>
          <GenericDataTable columns={columns} data={data} getRowBgColor={getRowBgColor} getCellProps={getCellProps} />
        </Box>
      )}
    </Box>
  );
});

ComparisionTable.displayName = 'ComparisionTable';
